interface LocalStorage<T> {
  key: string;
  data: T;
  timestamp: number;
}

interface ExpiringStorageValue {
  value: string;
  expiry: number;
}

const MAX_AGE_MS = 1 * 24 * 60 * 60 * 1000; // 1 day in milliseconds

/**
 * Get a string value from localStorage with expiry check
 * Returns null if the value doesn't exist or has expired
 */
export function getExpiringStorageItem(key: string): string | null {
  if (!key) return null;

  try {
    const item = localStorage.getItem(key);
    if (!item) return null;

    const parsed: ExpiringStorageValue = JSON.parse(item);
    if (Date.now() > parsed.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return parsed.value;
  } catch {
    // If parsing fails, remove the invalid item
    try {
      localStorage.removeItem(key);
    } catch {}
    return null;
  }
}

/**
 * Set a string value in localStorage with an expiry time
 * @param key - Storage key
 * @param value - Value to store (null to remove)
 * @param expiryDays - Number of days until expiry (default: 14)
 */
export function setExpiringStorageItem(
  key: string,
  value: string | null,
  expiryDays: number = 14,
): void {
  if (!key) return;

  try {
    if (value) {
      const expiryMs = expiryDays * 24 * 60 * 60 * 1000;
      const item: ExpiringStorageValue = {
        value,
        expiry: Date.now() + expiryMs,
      };
      localStorage.setItem(key, JSON.stringify(item));
    } else {
      localStorage.removeItem(key);
    }
  } catch {}
}

/**
 * Get data from localStorage
 */
export function getLocalStorageItem<T>(key: string): T | null {
  if (!key) return null;

  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const parsed: LocalStorage<T> = JSON.parse(stored);

    // Check if data is not too old (1 day)
    const age = Date.now() - parsed.timestamp;

    if (age < MAX_AGE_MS && parsed.data) {
      return parsed.data;
    } else {
      // Clear old data
      localStorage.removeItem(key);
      return null;
    }
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * Set data in localStorage
 */
export function setLocalStorageItem<T>(key: string, data: T): void {
  if (!key) return;

  try {
    const dataToStore: LocalStorage<T> = {
      key,
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(dataToStore));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    // If localStorage is full, try to clear old entries
    clearOldLocalStorageEntries();
  }
}

/**
 * Remove item from localStorage
 */
export function removeLocalStorageItem(key: string): void {
  if (key) {
    localStorage.removeItem(key);
  }
}

/**
 * Clear old entries to prevent localStorage bloat
 */
function clearOldLocalStorageEntries(): void {
  try {
    const now = Date.now();

    Object.keys(localStorage).forEach(k => {
      if (
        k.startsWith('callEdit_') ||
        k.startsWith('caseEdit_') ||
        k.startsWith('casesFilter_') ||
        k.startsWith('qcFilter_')
      ) {
        try {
          const item = localStorage.getItem(k);
          if (item) {
            const parsed = JSON.parse(item);
            if (parsed.timestamp && now - parsed.timestamp > MAX_AGE_MS) {
              localStorage.removeItem(k);
            }
          }
        } catch {
          // Remove invalid entries
          localStorage.removeItem(k);
        }
      }
    });
  } catch (error) {
    console.error('Error clearing old localStorage entries:', error);
  }
}

/**
 * Filter state to be cached
 */
export interface CachedFilterState {
  activeQuickFilter: string;
  searchValues: Record<string, string>;
  activeSearchType: string;
  assignedFilter: string[];
  dateFrom: string;
  dateTo: string;
  sortState: Array<{ field: string; direction: 'asc' | 'desc' }>;
}

/**
 * Save the last applied filter state for cases page
 */
export function saveCasesFilterCache(
  workflowId: string,
  programId: string,
  filterState: CachedFilterState,
): void {
  if (!workflowId || !programId) return;
  const key = `casesFilter_${programId}_${workflowId}`;
  setLocalStorageItem(key, filterState);
}

/**
 * Get the last applied filter state for cases page
 */
export function getCasesFilterCache(
  workflowId: string,
  programId: string,
): CachedFilterState | null {
  if (!workflowId || !programId) return null;
  const key = `casesFilter_${programId}_${workflowId}`;
  return getLocalStorageItem<CachedFilterState>(key);
}

/**
 * QC filter state to be cached (stores raw URL param values)
 */
export interface QcCachedFilterState {
  tab?: string;
  searchType?: string;
  callId?: string;
  evaluationId?: string;
  programIds?: string;
  agentIds?: string;
  callDirections?: string;
  scoreBand?: string;
  dateFrom?: string;
  dateTo?: string;
  sortByCallStartTime?: string;
  sortByAggregateScore?: string;
  sortByCallDuration?: string;
  sortBySectionScore?: string;
}

/**
 * Save the last applied filter state for QC evaluations page
 */
export function saveQcFilterCache(organizationId: string, filterState: QcCachedFilterState): void {
  if (!organizationId) return;
  const key = `qcFilter_${organizationId}`;
  setLocalStorageItem(key, filterState);
}

/**
 * Get the last applied filter state for QC evaluations page
 */
export function getQcFilterCache(organizationId: string): QcCachedFilterState | null {
  if (!organizationId) return null;
  const key = `qcFilter_${organizationId}`;
  return getLocalStorageItem<QcCachedFilterState>(key);
}
